import { AController } from "@eagerlogic/react-mvc";
import { CalendarModel, MonthSelectorModel, VacationCalendarModel, TeamModel, UserVacationModel, VacationDayModel, VacationDetailsDialogModel } from "./CalendarModel";
import { VacationData } from "data/VacationData";
import { ListAllResponse, TExtraWorkDayType, VacationTypeDto, VacationDto, VacationRpc } from "rpc/VacationRpc";
import { IntDate } from "utils/IntDate";
import { th } from "date-fns/esm/locale";
import { Meta, EUserRole } from "utils/meta";
import { UpcomingEventsData } from "data/UpcomingEventsData";
import { UserVacationStatisticsData } from "data/UserVacationStatisticsData";

export class CalendarController extends AController<CalendarModel> {

    private static vacationData: { [year: number]: VacationData } = null;

    public static emptyCache() {
        var cy = new Date().getFullYear();
        CalendarController.vacationData = [];
        CalendarController.vacationData[cy - 1] = new VacationData(cy - 1, 1000 * 60 * 60 * 2);
        CalendarController.vacationData[cy] = new VacationData(cy, 1000 * 60 * 10);
        CalendarController.vacationData[cy + 1] = new VacationData(cy + 1, 1000 * 60 * 60 * 2);
    }

    private readonly currentYear: number;
    private readonly currentMonth: number;

    private selectedYear: number;
    private selectedMonth: number;

    private searchTerm: string = "";

    public constructor(selectedYear?: number, selectedMonth?: number) {
        super();
        var date = new Date();
        this.currentYear = date.getFullYear();
        this.currentMonth = date.getMonth() + 1;

        if (selectedYear == null) {
            selectedYear = this.currentYear;
        }
        if (selectedYear < this.currentYear - 1) {
            selectedYear = this.currentYear - 1;
        }
        this.selectedYear = selectedYear;

        if (selectedMonth == null) {
            selectedMonth = this.currentMonth;
        }
        if (selectedMonth < 1) {
            selectedMonth = 1;
        }
        if (selectedMonth > 12) {
            selectedMonth = 12;
        }
        this.selectedMonth = selectedMonth;

        if (CalendarController.vacationData == null) {
            CalendarController.emptyCache();
        }
    }

    protected createInitialModel(): CalendarModel {
        var res = new CalendarModel(true, "", null, null);

        this.rerender();

        return res;
    }

    public onSelectedDatechanged(year: number, month: number) {
        this.selectedYear = year;
        this.selectedMonth = month;
        this.rerender();
    }

    public onSearchTermChanged(searchTerm: string) {
        this.searchTerm = searchTerm;
        this.rerender();
    }

    public showDetailsDialog(id: number, year: number, userId: number) {
        var vd = CalendarController.vacationData[year].get();
        var vdto: VacationDto = null;
        for (var t of vd.teams) {
            for (var u of t.users) {
                if (u.id != userId) {
                    continue;
                }
                for (var v of u.vacations) {
                    if (v.id != id) {
                        continue;
                    }

                    vdto = v;
                    break;
                }

                if (vdto != null) {
                    break;
                }
            }

            if (vdto != null) {
                break;
            }
        }

        if (vdto == null) {
            return;
        }

        var vacationTypeName = "Vacation";
        for (var vt of Meta.get().vacationTypes) {
            if (vt.id == vdto.vacationType) {
                vacationTypeName = vt.name;
                break;
            }
        }

        var sdStr = new IntDate(vdto.startDate).toString();
        var edStr = new IntDate(vdto.endDate).toString();

        var vm = new VacationDetailsDialogModel(
            vdto.id,
            vacationTypeName,
            vdto.halfDayType,
            sdStr,
            edStr,
            vdto.description,
            Meta.get().userRole.level >= EUserRole.HR.level || Meta.get().userId == userId
        )

        this.model.vacationDetailsDialogModel = vm;

        this.render(this.model);
    }

    public hideDetailsDialog() {
        this.model.vacationDetailsDialogModel = null;
        this.render(this.model);
    }

    public async deleteVacation(id: number) {
        try {
            await VacationRpc.delete(id);
            CalendarController.emptyCache();
            new UserVacationStatisticsData().invalidate();
            new UpcomingEventsData().invalidate();
            this.rerender();
        } catch (ex) {
            this.model.vacationDetailsDialogModel.globalError = "" + ex;
            this.render(this.model);
        }
    }

    public async rerender(): Promise<void> {
        var calData = await CalendarController.vacationData[this.selectedYear].getOrLoad();

        this.render(this.convertData(calData));
    }

    public colleapseTeam(id: number) {
        var collapsedTeams = this.readCollapsedTeams();
        collapsedTeams[id] = true;
        this.writeCollapsedTeams(collapsedTeams);
        this.rerender();
    }

    public expandTeam(id: number) {
        var collapsedTeams = this.readCollapsedTeams();
        collapsedTeams[id] = false;
        this.writeCollapsedTeams(collapsedTeams);
        this.rerender();
    }

    private readCollapsedTeams(): {[id: number]: boolean} {
        var dataStr = window.localStorage.getItem("calendar-collapsedTeams");
        if (dataStr == null) {
            return {};
        }

        return JSON.parse(dataStr);
    }

    private writeCollapsedTeams(data: {[id: number]: boolean}): void {
        if (data == null) {
            data = {};
        }
        window.localStorage.setItem("calendar-collapsedTeams", JSON.stringify(data));
    }

    private convertData(calData: ListAllResponse): CalendarModel {
        var collapsedTeams = this.readCollapsedTeams();

        var monthLength = this.getMonthLength(this.selectedYear, this.selectedMonth);

        var msm = new MonthSelectorModel(this.currentYear, this.selectedYear, this.selectedMonth);

        var holidays: TExtraWorkDayType[] = [];
        for (var i = 0; i < monthLength; i++) {
            holidays.push(calData.extraWorkDays[this.selectedMonth - 1][i]);
        }

        var workDays: boolean[] = new Array(monthLength);
        var dayIndex: number[] = new Array(monthLength);
        var currWeekDay = new Date(this.selectedYear, this.selectedMonth - 1, 1).getDay();
        if (currWeekDay == 0) {
            currWeekDay = 7;
        }
        for (var d = 1; d <= monthLength; d++) {
            workDays[d - 1] = calData.workDays[currWeekDay - 1];
            dayIndex[d - 1] = currWeekDay;

            currWeekDay++;
            if (currWeekDay > 7) {
                currWeekDay = 1;
            }
        }

        var vcm = new VacationCalendarModel(monthLength, holidays, workDays, dayIndex);

        for (var t of calData.teams) {
            var tm = new TeamModel(t.id, t.name);
            tm.collapsed = collapsedTeams[t.id] == true;

            let cuHere = false;
            for (var u of t.users) {
                if (!this.isMatch(u.name, this.searchTerm)) {
                    continue;
                }
                var um = new UserVacationModel(monthLength, u.id, u.name, u.position);
                if (u.id == Meta.get().userId) {
                    cuHere = true;
                    tm.users.unshift(um);
                } else {
                    tm.users.push(um);
                }

                for (var v of u.vacations) {
                    var sd = new IntDate(v.startDate);
                    var ed = new IntDate(v.endDate);
                    if (!this.isIntersectsMonth(this.selectedYear, this.selectedMonth, sd, ed)) {
                        continue;
                    }

                    for (var d = 1; d <= monthLength; d++) {
                        if (!this.isIntersectsDay(new IntDate(this.selectedYear * 10000 + this.selectedMonth * 100 + d), sd, ed)) {
                            continue;
                        }

                        var idx = d - 1;
                        um.days.ids[idx] = v.id;
                        um.days.halfDays[idx] = v.halfDayType;
                        um.days.descriptions[idx] = v.description;
                        um.days.types[idx] = this.searchVacationType(v.vacationType, calData.vacationTypes);
                    }

                }
            }

            if (this.searchTerm != null && this.searchTerm.trim().length > 0 && tm.users.length > 0) {
                tm.collapsed = false;
            } 

            if (cuHere) {
                vcm.teams.unshift(tm);
            } else {
                vcm.teams.push(tm);
            }
        }

        var res = new CalendarModel(false, this.searchTerm, msm, vcm);

        return res;
    }

    private getMonthLength(year: number, month: number) {
        if (month == 2 && year % 4 == 0) {
            return 29;
        }

        var monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        return monthLengths[month - 1];

    }

    private isIntersectsMonth(y: number, m: number, sd: IntDate, ed: IntDate) {
        if ((sd.year < y && ed.year < y) || (sd.year > y && ed.year > y)) {
            return false;
        }

        if (sd.year == y && sd.month > m) {
            return false;
        }

        if (ed.year == y && ed.month < m) {
            return false;
        }

        return true;
    }

    private isIntersectsDay(cd: IntDate, sd: IntDate, ed: IntDate): boolean {
        if (sd.date <= cd.date && ed.date >= cd.date) {
            return true;
        }

        return false;
    }

    private searchVacationType(id: string, types: VacationTypeDto[]) {
        for (var vt of types) {
            if (vt.id == id) {
                return vt;
            }
        }

        return null;
    }

    private isMatch(value: string, searchTerm: string) {
        var st = searchTerm.trim();
        st = st.toLowerCase();
        if (st.length < 1) {
            return true;
        }

        var sts = st.split(" ");
        value = value.toLowerCase();

        for (var s of sts) {
            if (s.length < 1) {
                continue;
            }

            if (value.indexOf(s) < 0) {
                return false;
            }
        }

        return true;
    }

}